import { useMemo } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type {
  MatchPublic,
  PlayerPublic,
  PredictionPublic,
  TeamPublic,
} from '@mondy/shared';
import { api } from '@/api';
import { ApiError } from '@mondy/shared/api-client';
import { useNavHistory } from '@/state/navHistory';
import {
  buildGroups,
  GroupCard,
  ThirdPlaceLegend,
} from '@/components/GroupStandings';
import { MatchDayList } from '@/components/MatchDayList';
import { Spinner } from '@/components/Spinner';
import { TeamFlag } from '@/components/TeamFlag';
import { useCurrentUser } from '@/hooks/useCurrentUser';

interface SquadResponse {
  team: TeamPublic;
  players: PlayerPublic[];
}

interface TeamsResponse {
  teams: TeamPublic[];
}

interface MatchListResponse {
  matches: MatchPublic[];
}

const POSITION_LABELS: Record<PlayerPublic['position'], string> = {
  GK: 'Goalkeepers',
  DEF: 'Defenders',
  MID: 'Midfielders',
  FWD: 'Forwards',
};

const POSITION_ORDER: PlayerPublic['position'][] = ['GK', 'DEF', 'MID', 'FWD'];

export function TeamDetailPage() {
  const { goBack } = useNavHistory();
  const { id } = useParams<{ id: string }>();
  const user = useCurrentUser();
  const query = useQuery({
    queryKey: ['team', id, 'squad'],
    queryFn: () => api.get<SquadResponse>(`/api/teams/${id}/squad`),
    enabled: Boolean(id),
    staleTime: 30 * 60 * 1000,
  });

  const matchesQuery = useQuery({
    queryKey: ['matches', { teamId: id }],
    queryFn: () => api.get<MatchListResponse>(`/api/matches?teamId=${id}`),
    enabled: Boolean(id),
  });

  // Reuse the exact query keys from the Teams page so TanStack Query serves
  // these from cache when the user navigated in from /teams — no extra fetch.
  const teamsQuery = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.get<TeamsResponse>('/api/teams'),
    staleTime: 60 * 60 * 1000,
  });

  const groupMatchesQuery = useQuery({
    queryKey: ['matches', { stage: 'group' }],
    queryFn: () => api.get<MatchListResponse>('/api/matches?stage=group'),
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  });

  const groups = useMemo(
    () =>
      teamsQuery.data
        ? buildGroups(
            teamsQuery.data.teams,
            groupMatchesQuery.data?.matches ?? [],
          )
        : [],
    [teamsQuery.data, groupMatchesQuery.data],
  );

  const predictionsQuery = useQuery({
    queryKey: ['predictions', 'mine'],
    queryFn: () => api.get<{ predictions: PredictionPublic[] }>('/api/predictions/mine'),
    enabled: !!user,
  });

  const predictionsByMatch = useMemo(() => {
    const map = new Map<string, PredictionPublic>();
    if (predictionsQuery.data) {
      for (const p of predictionsQuery.data.predictions) {
        map.set(p.matchId, p);
      }
    }
    return map;
  }, [predictionsQuery.data]);

  // Hold a single full-screen spinner until everything feeding the first paint
  // has settled — squad, the group-standings feeders (teams + group matches),
  // and this team's match list. Otherwise the page renders on the squad query
  // alone and the group standings / matches pop in afterward (the group card
  // would even flash all-zero rows before group matches arrive). Mirrors how
  // /standings gates the whole page on its loading state.
  if (
    query.isLoading ||
    teamsQuery.isLoading ||
    groupMatchesQuery.isLoading ||
    matchesQuery.isLoading
  ) {
    return <Spinner fullScreen label="Loading team" />;
  }
  if (query.isError) {
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    return (
      <div style={{ textAlign: 'center', padding: '64px 0' }}>
        <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
          {notFound ? 'Team not found' : 'Something went wrong'}
        </p>
        <Link to="/teams" className="btn btn-ghost">
          Back to teams
        </Link>
      </div>
    );
  }

  const { team, players } = query.data!;
  const groupStandings = team.group
    ? groups.find((g) => g.group === team.group)
    : undefined;

  return (
    <>
      <div className="page-head">
        <div className="page-title">
          <div className="k">
            <button
              type="button"
              onClick={() => goBack('/teams')}
              style={{
                color: 'inherit',
                textDecoration: 'none',
                marginRight: 8,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                font: 'inherit',
                padding: 0,
              }}
            >
              ←
            </button>
            {team.group ? `Group ${team.group}` : 'Team'}
            {team.group && team.confederation ? ' · ' : ''}
            {team.confederation ?? ''}
          </div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <TeamFlag
              code={team.fifaCode}
              url={team.flagUrl}
              name={team.name}
              size="lg"
            />
            {team.name}
          </h1>
        </div>
      </div>

      {groupStandings ? (
        <section className="mb-8 flex flex-col gap-3">
          <GroupCard {...groupStandings} />
          {groupStandings.rows[2]?.thirdPlaceQualifying ? (
            <ThirdPlaceLegend />
          ) : null}
        </section>
      ) : null}

      <section className="mb-8 flex flex-col gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-fg-muted)]">
          Matches
        </h2>
        {matchesQuery.isError ? (
          <p className="py-6 text-center text-sm text-[color:var(--danger)]">
            Couldn't load matches. Try again in a moment.
          </p>
        ) : (matchesQuery.data?.matches.length ?? 0) === 0 ? (
          <p className="py-6 text-center text-sm text-[color:var(--color-fg-muted)]">
            No matches scheduled yet.
          </p>
        ) : (
          <MatchDayList
            matches={matchesQuery.data!.matches}
            predictionsByMatch={predictionsByMatch}
          />
        )}
      </section>

      {players.length === 0 ? (
        <section className="rounded-xl border border-dashed border-[color:var(--color-border)] p-6 text-center text-sm text-[color:var(--color-fg-muted)]">
          Squad not yet announced. FIFA requires squads by late May 2026 — this
          page will populate automatically once the admin imports the final
          squad data.
        </section>
      ) : (
        <div className="flex flex-col gap-5">
          {POSITION_ORDER.map((pos) => {
            const byPosition = players.filter((p) => p.position === pos);
            if (byPosition.length === 0) return null;
            return (
              <section key={pos} className="flex flex-col gap-2">
                <h2 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-fg-muted)]">
                  {POSITION_LABELS[pos]}
                </h2>
                <ul className="flex flex-col gap-1">
                  {byPosition.map((p) => (
                    <li
                      key={p.id}
                      className="flex items-center justify-between gap-3 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 py-2"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="w-8 text-center text-xs text-[color:var(--color-fg-muted)] tabular-nums">
                          {p.jerseyNumber ?? '-'}
                        </span>
                        <span className="truncate font-medium">{p.name}</span>
                      </div>
                      {p.club ? (
                        <span className="shrink-0 text-xs text-[color:var(--color-fg-muted)]">
                          {p.club}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </>
  );
}
